import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import KaboodleModal from "components/Modals/KaboodleModal";
import Table, { SelectColumnNoSelectAll } from "components/Table/Table";
import { useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useGetDocumentsQuery } from "services/documents-api";
import {
  useGetLienByIdQuery,
  useTriggerLienFileOCRMutation,
  useTriggerOCRLienMutation,
} from "services/lien-api";

import type { Column } from "react-data-grid";
import type { DocumentType } from "components/Documents/DocumentTypes";

type TriggerOCRModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
};

const TriggerOCRModal = ({
  isOpen,
  onClose,
  onSuccess,
}: TriggerOCRModalProps) => {
  const { lienFileId, lienId } = useParams();
  const [searchParams] = useSearchParams();
  const fileId = searchParams.get("file_id");
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<Set<number>>(
    new Set(),
  );
  const [triggerOCRLien] = useTriggerOCRLienMutation();
  const [triggerLienFileOCR] = useTriggerLienFileOCRMutation();
  const [isProcessing, setIsProcessing] = useState(false);

  // if we're on a lien detail page, we need to get the file id from the lien
  const { data: lienData } = useGetLienByIdQuery(
    { lienId: lienId || "" },
    { skip: !lienId },
  );

  // if we're on the overview page, use the file_id from query params
  // if we're on a lien detail page, use the file id from the lien data
  const documentFileId = lienId ? lienData?.file?.id : Number(fileId);

  // fetch documents independently when modal is open
  const { data: documentsData, isLoading } = useGetDocumentsQuery(
    {
      fileId: documentFileId?.toString() || "",
      sortBy: "-id",
      limit: "1000", // get all documents for OCR selection
      offset: "0",
    },
    {
      skip: !documentFileId || !isOpen,
      refetchOnMountOrArgChange: true,
    },
  );

  const documents = documentsData?.results || [];

  // filter out AI-generated documents and non-PDF files since they shouldn't be used for OCR
  const filteredDocuments = useMemo(
    () =>
      documents.filter(
        (doc) =>
          doc.type !== "AI_ALLOCATOR_NOTES" &&
          doc.type !== "AI_SORTED_AND_CLASSIFIED_DOCUMENTS" &&
          doc.extension === ".pdf",
      ),
    [documents],
  );

  const columns: Column<DocumentType>[] = [
    {
      ...SelectColumnNoSelectAll,
      minWidth: 35,
      width: 35,
    },
    {
      key: "original_filename",
      name: "Document Name",
    },
    {
      key: "created",
      name: "Created",
      renderCell: ({ row }) => new Date(row.created).toLocaleDateString(),
    },
  ];

  const handleClose = () => {
    setSelectedDocumentIds(new Set());
    setIsProcessing(false);
    onClose();
  };

  const handleTriggerOCR = async () => {
    if (selectedDocumentIds.size === 0) {
      KaboodleToaster.error("Please select a document");
      return;
    }

    try {
      setIsProcessing(true);

      const data = {
        document_ids: Array.from(selectedDocumentIds),
      };

      if (lienId) {
        // we're on a lien detail page
        await triggerOCRLien({
          lienId,
          data,
        }).unwrap();
      } else if (lienFileId) {
        // we're on the overview page
        await triggerLienFileOCR({
          lienFileId: Number(lienFileId),
          data,
        }).unwrap();
      }

      KaboodleToaster.success("OCR processing started");
      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    } catch (err) {
      console.error("Failed to trigger OCR:", err);
      KaboodleToaster.error(
        "Failed to start OCR processing. Please try again.",
      );
      setIsProcessing(false);
    }
  };

  const handleSelectedRowsChange = (selectedRows: Set<string | number>) => {
    // only allow single selection - if multiple are being selected, just take the last one
    const rowsArray = Array.from(selectedRows) as number[];
    const singleSelection =
      rowsArray.length > 0 ? [rowsArray[rowsArray.length - 1]] : [];
    setSelectedDocumentIds(new Set(singleSelection));
  };

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={handleClose}
      footer={
        <div className="flex jc-c">
          <Button onClick={handleTriggerOCR} disabled={isProcessing}>
            {isProcessing ? "Processing..." : "Trigger OCR"}
          </Button>
          <Button onClick={handleClose}>Cancel</Button>
        </div>
      }
    >
      <Card heading="Trigger OCR">
        <div className="flex flex-col gap-4">
          {isLoading ? (
            <div>Loading documents...</div>
          ) : filteredDocuments.length === 0 ? (
            <div>No documents found</div>
          ) : (
            <Table
              columns={columns}
              rows={filteredDocuments}
              rowKeyGetter={(row) => row.id}
              selectedRows={selectedDocumentIds}
              onSelectedRowsChange={handleSelectedRowsChange}
              className="h-full"
            />
          )}
        </div>
      </Card>
    </KaboodleModal>
  );
};

export default TriggerOCRModal;
